<template>
  <ion-page>
    <ion-content
      class="main-background-dark"
      :fullscreen="true"
      slot="fixed"
      :scroll-y="false"
    >
      <div class="release-safe-area"></div>
      
      <!-- Header Section with Logo -->
      <div class="header-wrapper">
        <div class="logo-wrapper">
          <img
            :src="programLogo || getProgramImage(releaseEntity.programShortName)"
            alt="program logo"
          />
        </div>
        <div class="duration-badge">
          <ion-icon :icon="timeOutline"></ion-icon>
          <span>{{ mediaUtils.formatTime(mediaUtils.getTotalPlaylistDuration(musicList)) }}</span>
      </div>
      </div>

      <!-- Music List with Modern Design -->
      <ion-content class="bg-transparent music-list">
        <ion-list class="bg-transparent" lines="none">
          <ion-item
            v-for="(audioTrack, idx) in musicList"
            :key="idx"
            @click="selectTrack(idx)"
            class="music-item"
            :class="{ 'playing': isTrackRowActive(idx) }"
          >
            <!-- Track Number -->
            <div class="track-number">
              <span v-if="!isTrackRowActive(idx)">{{ idx + 1 }}</span>
              <ion-icon
                v-else
                :icon="playerBar.playing ? pause : play"
                class="playing-icon"
              ></ion-icon>
            </div>

            <!-- Track Info -->
            <div class="track-info">
              <div class="track-name">{{ mediaUtils.getTrackName(audioTrack) }}</div>
              <div class="track-duration">{{ getTrackDuration(audioTrack) }}</div>
            </div>

            <!-- Offline Indicator -->
            <div class="track-actions">
            <ion-icon
                v-if="audioTrack.isOffline"
              :icon="cloudDone"
                class="offline-icon"
              ></ion-icon>
              <div class="play-button-wrapper">
                <ion-icon
                  :icon="isTrackRowActive(idx) && playerBar.playing ? pause : play"
                  class="play-icon"
                ></ion-icon>
              </div>
            </div>
          </ion-item>
        </ion-list>
        <ion-item class="fake-footer bg-transparent" lines="none"></ion-item>
      </ion-content>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { PlayerBar } from "@/model/playerbar.model";
import { Release } from "@/model/release.model";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import {
    IonContent,
    IonIcon,
    IonItem,
    IonList,
    IonPage,
} from "@ionic/vue";
import axios from "axios";
import { cloudDone, pause, play, timeOutline } from "ionicons/icons";
import { defineComponent, inject } from "vue";

import { GlobalContext } from "@/model/globalcontext.model";
import { GoogleMedia } from "@/model/googlemedia.model";
import { PlayerModes } from "@/model/player.mode";
import { Playlist } from "@/model/playlist.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import LogoService from "@/services/logo.service";
import MediaUtils from "@/services/media.utils";
import UnicodeUtils from "@/services/unicode.utils";
import { CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { RmxAudioPlayer } from "capacitor-plugin-playlist";
// @ts-ignore
import { MittEmitter } from "mitt-emitter";

export default defineComponent({
  name: "ReleaseMusic",
  components: {
    IonContent,
    IonPage,
    IonList,
    IonItem,
    IonIcon,
  },
  emits: ["reloadMusicPlayer"],

  setup() {
    const playerBar = inject("playerBar") as PlayerBar;
    const audioPlayer = inject("audioPlayer") as RmxAudioPlayer;
    const releaseEntity = inject("release") as Release;
    const emitter = inject("emitter") as MittEmitter;
    const filesystemCustomUtils = new FilesystemCustomUtils();
    const unicodeUtils = new UnicodeUtils();
    const mediaUtils = new MediaUtils();
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;

    return {
      globalContext,
      playerBar,
      audioPlayer,
      releaseEntity,
      emitter,
      cloudDone,
      play,
      pause,
      timeOutline,
      filesystemCustomUtils,
      unicodeUtils,
      mediaUtils,
      isMobile,
    };
  },

  data() {
    return {
      logoPath: "",
      isFirstSelect: true,
      musicList: Array<GoogleMedia>(),
      programLogo: "",
      logoService: new LogoService(),
    };
  },

  ionViewWillEnter() {
    this.playerBar.fixedAlbumArtUrl = '';
    this.logoPath = `@/theme/programs/${this.releaseEntity.programShortName}.png`;
    this.playerBar.visible = this.playerBar.isReady && this.playerBar.musicList.length > 0;
    this.playerBar.currentMode = PlayerModes.EXTENDED;
    this.loadProgramLogo();
  },

  ionViewDidEnter() {
    this.globalContext.isLoading = false;
  },

  mounted() {
    this.loadMusicList();
    this.isFirstSelect = true;
    this.playerBar.visible = false;
    this.playerBar.isReady = false;
  },

  methods: {
    getReleaseImage() {
      return `${process.env.VUE_APP_API_EXT_URL}api/google/file/name/${this.releaseEntity.programShortName}_${this.releaseEntity.releaseWithZero}.png`;
    },
    getTrackDuration(audioTrack: GoogleMedia) {
      return this.mediaUtils.formatTime(audioTrack.duration);
    },
    loadMusicList() {
      this.playerBar.visible = true;
      this.getMusicList();
    },
    async getMusicList() {
      this.musicList = new Array<GoogleMedia>();
      
      // Check if release uses server storage
      if (this.releaseEntity.contentSource === 'SERVER') {
        // Use server API
        if (this.isMobile) {
          if (this.releaseEntity.musicOffline) {
            // Load offline music from local storage
            console.log("Found offline directory: " + this.releaseEntity.musicDirectory);
            
            Filesystem.readdir({
              path: `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}`,
              directory: Directory.Data,
            })
              .then((res) => {
                const foundPlaylist = res.files.find((f) => {
                  return f.name == this.filesystemCustomUtils.PLAYLIST_PATH;
                });

                if (typeof foundPlaylist !== "undefined" && null != foundPlaylist) {
                  Filesystem.readFile({
                    path: `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}/${this.filesystemCustomUtils.PLAYLIST_PATH}`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                  })
                    .then((res) => {
                      const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
                      if (localPlaylist) {
                        this.musicList = localPlaylist.tracks;
                        this.playerBar.playlistName = localPlaylist.name;
                        this.playerBar.playlistImageDir = this.releaseEntity.imageDirectory;
                        this.playerBar.playlistImage = this.releaseEntity.imagePath;
                        this.playerBar.programShortName = this.releaseEntity.programShortName;
                        const filesystemCustomUtils = new FilesystemCustomUtils();
                        filesystemCustomUtils
                          .isPlaylistOffline(
                            this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
                            localPlaylist
                          )
                          .then((response) => {
                            localPlaylist.offline = response;
                            this.musicList = this.musicList.map<GoogleMedia>(
                              (musicTrack) => {
                                musicTrack.isOffline = localPlaylist.offline;
                                return musicTrack;
                              }
                            );
                          });
                      }
                    })
                    .catch((error) => {
                      console.error("Failed to load local playlist", error);
                    });
                }
              })
              .catch(() => {
                // not exists or not a dir
              });
          } else {
            // Load from server API (mobile)
            const tokenQuery = await Preferences.get({ key: "jwt" });
            const token = tokenQuery.value;

            const options = {
              url: `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/media/music`,
              headers: { Authorization: `Bearer ${token}` },
              readTimeout: 60000,
            };

            await CapacitorHttp.get(options)
              .then((response) => {
                this.musicList = response.data;
                this.playerBar.playlistImageDir = this.releaseEntity.imageDirectory;
                this.playerBar.playlistImage = this.releaseEntity.imagePath;
                this.playerBar.programShortName = this.releaseEntity.programShortName;
              })
              .catch(() => {
                this.$router.replace("/badconnection");
              });
          }
        } else {
          // Load from server API (web)
          axios
            .get(`/api/releases/${this.releaseEntity.id}/media/music`)
            .then((response) => {
              this.musicList = response.data;
              this.playerBar.playlistImage = this.releaseEntity.imagePath;
              this.playerBar.programShortName = this.releaseEntity.programShortName;
            })
            .catch((err) => {
              console.log("call error " + err);
            });
        }
        return;
      }
      
      // Original Google Drive logic (for backward compatibility)
      if (this.isMobile) {
        if (this.releaseEntity.musicOffline) {
          //load offline music
          console.log(
            "Found offline directory: " + this.releaseEntity.musicDirectory
          );

          Filesystem.readdir({
            path: `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}`,
            directory: Directory.Data,
          })
            .then((res) => {
              const foundPlaylist = res.files.find((f) => {
                return f.name == this.filesystemCustomUtils.PLAYLIST_PATH;
              });

              if (
                typeof foundPlaylist !== "undefined" &&
                null != foundPlaylist
              ) {
                Filesystem.readFile({
                  path: `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}/${this.filesystemCustomUtils.PLAYLIST_PATH}`,
                  directory: Directory.Data,
                  encoding: Encoding.UTF8,
                })
                  .then((res) => {
                    const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
                    if (localPlaylist) {
                      this.musicList = localPlaylist.tracks;
                      this.playerBar.playlistName = localPlaylist.name;
                      this.playerBar.playlistImageDir =
                        this.releaseEntity.imageDirectory;
                      this.playerBar.playlistImage = this.releaseEntity.imagePath;
                      this.playerBar.programShortName = this.releaseEntity.programShortName;
                      const filesystemCustomUtils = new FilesystemCustomUtils();
                      filesystemCustomUtils
                        .isPlaylistOffline(
                          this.filesystemCustomUtils.PLAYLISTS_DIRECTORY,
                          localPlaylist
                        )
                        .then((response) => {
                          localPlaylist.offline = response;

                          this.musicList = this.musicList.map<GoogleMedia>(
                            (musicTrack) => {
                              musicTrack.isOffline = localPlaylist.offline;
                              return musicTrack;
                            }
                          );
                        });
                    }
                  })
                  .catch((error) => {
                    console.error("Failed to load local playlist", error);
                  });
              }
            })
            .catch(() => {
              // not exists or not a dir
            });
        } else {
          const tokenQuery = await Preferences.get({ key: "jwt" });
          const token = tokenQuery.value;

          const options = {
            url: `${process.env.VUE_APP_API_URL}api/google/media/${this.releaseEntity.musicDirectory}`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          };

          await CapacitorHttp.get(options)
            .then((response) => {
              this.musicList = response.data;
              this.playerBar.playlistImageDir =
                this.releaseEntity.imageDirectory;
              this.playerBar.playlistImage = this.releaseEntity.imagePath;
              this.playerBar.programShortName = this.releaseEntity.programShortName;
            })
            .catch(() => {
              this.$router.replace("/badconnection");
            });
        }
      } else {
        axios
          .get(`/api/google/media/${this.releaseEntity.musicDirectory}`)
          .then((response) => {
            this.musicList = response.data;
            this.playerBar.playlistImage = this.releaseEntity.imagePath;
            this.playerBar.programShortName = this.releaseEntity.programShortName;
          })
          .catch((err) => {
            console.log("call error " + err);
          });
      }
    },

    isTrackRowActive(idx: number): boolean {
      return (
        !this.isFirstSelect &&
        this.playerBar.currentTrackIdx === idx &&
        !!(this.playerBar.visible || this.playerBar.microMode)
      );
    },

    selectTrack(idx: number) {
      const expectedDir = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}`;
      if (
        !this.isFirstSelect &&
        this.playerBar.offlineDirectory === expectedDir &&
        this.playerBar.currentTrackIdx === idx
      ) {
        if (this.playerBar.playing) {
          this.emitter.emit("music-player-pause");
        } else {
          this.emitter.emit("music-player-play");
        }
        return;
      }

      if (this.audioPlayer && this.audioPlayer.isPlaying) {
        this.audioPlayer.pause();
      }

      this.playerBar.visible = true;
      this.playerBar.isFM = false;
      this.playerBar.currentTrackIdx = idx;
      this.playerBar.playlistName = `${this.releaseEntity.programName} ${this.releaseEntity.release}`;
      this.playerBar.offline = this.releaseEntity.musicOffline;
      this.playerBar.offlineDirectory = `${this.filesystemCustomUtils.PLAYLISTS_DIRECTORY}/${this.releaseEntity.musicDirectory}`;

      if (this.isFirstSelect) {
        this.isFirstSelect = false;
        this.playerBar.musicList = this.musicList;
        this.emitter.emit("music-player-open-extended", true);
        this.emitter.emit("music-player-loaded-list", true);
      } else {
        this.emitter.emit("music-player-open-extended", true);
        this.emitter.emit("music-player-restart-and-play", true);
      }
    },

    async loadProgramLogo() {
      if (this.releaseEntity.programShortName) {
        try {
          this.programLogo = await this.logoService.getProgramLogo(this.releaseEntity.programShortName);
        } catch (error) {
          console.error(`Failed to load logo for ${this.releaseEntity.programShortName}:`, error);
        }
      }
    },

    getProgramImage(shortName: string | undefined): string {
      if (!shortName || shortName.trim() === '') {
        return require('@/theme/liga-logo-white.png');
      }
      
      try {
        return require(`@/theme/programs/${shortName}.png`);
      } catch (error) {
        console.warn(`Program image not found for ${shortName}, using Liga logo as fallback`);
        return require('@/theme/liga-logo-white.png');
      }
    },
  },
});
</script>

<style scoped>
.release-safe-area {
  width: 100vw;
  height: var(--ion-safe-area-top);
}

/* Header Section */
.header-wrapper {
  margin-top: 8vh;
  margin-bottom: 3vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.logo-wrapper {
  width: 60%;
  max-width: 300px;
  height: auto;
}

.logo-wrapper img {
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
}

.duration-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1.2rem;
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  font-weight: 500;
  font-family: 'Montserrat', sans-serif;
}

.duration-badge ion-icon {
  font-size: 1.2rem;
}

/* Music List */
.music-list {
  max-height: 75vh;
}

.fake-footer {
  height: 25vh;
}

/* Track Item */
.music-item {
  --background: rgba(255, 255, 255, 0.05);
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
  margin-bottom: 8px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.music-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    rgba(255, 140, 0, 0) 0%, 
    rgba(255, 140, 0, 0.05) 50%, 
    rgba(255, 140, 0, 0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.music-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 140, 0, 0.3);
  transform: translateX(4px);
}

.music-item:hover::before {
  opacity: 1;
}

.music-item.playing {
  background: rgba(255, 140, 0, 0.15);
  border-color: rgba(255, 140, 0, 0.4);
}

.music-item.playing .track-number span {
  opacity: 0;
}

.music-item.playing .playing-icon {
  opacity: 1;
  color: var(--ion-color-orange);
}

/* Track Layout */
.music-item {
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

/* Track Number */
.track-number {
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  font-family: 'Montserrat', sans-serif;
}

.track-number span {
  transition: opacity 0.2s ease;
}

.playing-icon {
  opacity: 0;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  color: var(--ion-color-orange);
}

/* Track Info */
.track-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-left: 16px;
  overflow: hidden;
}

.track-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Montserrat', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.playing .track-name {
  color: var(--ion-color-orange);
  font-weight: 600;
}

.track-duration {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Montserrat', sans-serif;
  font-weight: 400;
}

.playing .track-duration {
  color: rgba(255, 140, 0, 0.8);
}

/* Track Actions */
.track-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.offline-icon {
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.6);
  opacity: 0.7;
}

.play-button-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 140, 0, 0.15);
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0;
  transform: scale(0.8);
}

.music-item:hover .play-button-wrapper,
.music-item.playing .play-button-wrapper {
  opacity: 1;
  transform: scale(1);
  background: rgba(255, 140, 0, 0.25);
}

.play-icon {
  font-size: 1.2rem;
  color: var(--ion-color-orange);
}

/* Responsive Design */
@media (min-width: 768px) {
  .header-wrapper {
    margin-top: 6vh;
  }
  
  .logo-wrapper {
    width: 35%;
    max-width: 400px;
  }

  .music-item {
    --min-height: 72px;
  }

  .track-name {
    font-size: 1rem;
  }
}

@media (min-width: 1025px) {
  .header-wrapper {
    margin-top: 5vh;
  }
  
  .music-item {
    padding: 0 24px;
  }

  .track-info {
    margin-left: 24px;
  }
}

/* Dark mode optimizations */
@media (prefers-color-scheme: light) {
  .music-item {
    --background: rgba(0, 0, 0, 0.1);
  }
  
  .duration-badge {
    background: rgba(255, 255, 255, 0.15);
  }
}

/* Animation for playing state */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.playing .playing-icon {
  animation: pulse 2s ease-in-out infinite;
}
</style>